import React from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import type { RootState } from "../../../store/store";
import { isFinanceiroOrAdminFromToken } from "../../../utils/authToken";
import { ENTRY_STATUS_BADGE, statusLabel } from "../../financeiro/api/finTypes";
import { FIN_ENTRY_PAGE } from "../../financeiro/routes";
import type { DpFinanceView, DpGuideFinance } from "../api/financeBridge";
import { formatCurrency, formatDateOnly } from "../utils/format";
// Tabela que vira cartões no celular (mesmo padrão da lista do Financeiro).
import "../../financeiro/pages/Financeiro.css";

type Props = {
  view: DpFinanceView | null | undefined;
  /** Texto curto do que gera os títulos (ex.: "Calcular e Fechar atualizam a previsão"). */
  hint?: string;
};

/**
 * Linha "Financeiro" das telas do DP (ADR 0029): os títulos de previsão que a ação do
 * DP criou no Financeiro, a situação de cada um e os avisos de divergência. O link
 * para o título aparece só para quem abre o Financeiro.
 */
export default function FinanceBridgeBox({ view, hint }: Props) {
  const token = useSelector((s: RootState) => s.auth.accessToken);
  const canOpen = isFinanceiroOrAdminFromToken(token);
  if (!view || view.items.length === 0) return null;

  return (
    <section className="dpSection" aria-labelledby="dp-financeiro">
      <h3 id="dp-financeiro" className="dpSection__title">
        Financeiro
      </h3>
      {hint ? <p className="dpFormHint" style={{ marginTop: 0 }}>{hint}</p> : null}
      {view.warnings.map((w, i) => (
        <p key={i} className="dpNotice dpNotice--warning">
          {w}
        </p>
      ))}
      <div className="ecoTableWrap">
        <table className="ecoTable finTable--cards" style={{ tableLayout: "fixed" }}>
          {/* Larguras fixas e tudo alinhado à esquerda, sob o cabeçalho: valor à direita ao lado de data à
              esquerda abria vazios desiguais entre as colunas. */}
          <colgroup>
            <col style={{ width: "13%" }} />
            <col style={{ width: "25%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "18%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Título</th>
              <th>Centro de custo</th>
              <th>Previsão</th>
              <th>Vencimento</th>
              <th>No Financeiro</th>
              <th>Situação</th>
            </tr>
          </thead>
          <tbody>
            {view.items.map((item) => (
              <tr key={item.sourceRef}>
                <td data-label="Título">{item.label}</td>
                <td data-label="Centro de custo" className="ecoTable__muted">
                  {item.costCenter ? `${item.costCenter.code} — ${item.costCenter.name}` : "—"}
                </td>
                <td data-label="Previsão" style={{ fontVariantNumeric: "tabular-nums" }}>{formatCurrency(item.expectedAmount)}</td>
                <td data-label="Vencimento">{formatDateOnly(item.entry?.dueDate ?? item.expectedDueDate)}</td>
                <td data-label="No Financeiro" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {item.entry ? formatCurrency(item.entry.amount) : "—"}
                </td>
                <td data-label="Situação">
                  {item.entry ? (
                    <>
                      <span className={`dpBadge ${ENTRY_STATUS_BADGE[item.entry.status]}`}>
                        {statusLabel(item.entry.status, "PAGAR")}
                        {item.entry.status === "PAGO" && item.entry.lastPaidAt ? ` em ${formatDateOnly(item.entry.lastPaidAt)}` : ""}
                      </span>
                      {canOpen ? (
                        <Link to={FIN_ENTRY_PAGE(item.entry.entryId)} className="dpFormHint" style={{ marginLeft: 8, whiteSpace: "nowrap" }}>
                          Abrir no Financeiro
                        </Link>
                      ) : null}
                    </>
                  ) : (
                    <span className="ecoTable__muted">Sem título</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Coluna "Financeiro" de Recolhimentos: a situação do título da guia, a divergência
 * entre a guia e o título já baixado, e o aviso de "pago no DP sem baixa".
 */
export function GuideFinanceCell({ finance, guideTotal, warning }: { finance: DpGuideFinance; guideTotal: string; warning: string | null }) {
  const token = useSelector((s: RootState) => s.auth.accessToken);
  const canOpen = isFinanceiroOrAdminFromToken(token);
  if (!finance) return <span className="ecoTable__muted">Sem título</span>;
  const settled = Number(finance.settledAmount);
  const diverges = settled > 0 && Number(finance.amount) !== Number(guideTotal);
  return (
    <>
      <span className={`dpBadge ${ENTRY_STATUS_BADGE[finance.status]}`}>
        {finance.status === "PAGO" && finance.lastPaidAt
          ? `Pago em ${formatDateOnly(finance.lastPaidAt)}`
          : `${statusLabel(finance.status, "PAGAR")} · vence ${formatDateOnly(finance.dueDate)}`}
      </span>
      {diverges ? (
        <div className="dpFormHint dpFormHint--warning">
          Baixado com {formatCurrency(finance.settledAmount)}; a guia agora é {formatCurrency(guideTotal)}.
        </div>
      ) : null}
      {warning ? <div className="dpFormHint dpFormHint--warning">{warning}</div> : null}
      {finance.allocations?.length ? (
        <div
          className="dpFormHint"
          title={finance.allocations.map((x) => `${x.costCenter.code} ${x.costCenter.name}: ${formatCurrency(x.amount)}`).join(" · ")}
        >
          Rateado em {finance.allocations.length} centros de custo
        </div>
      ) : finance.costCenter ? (
        <div className="dpFormHint">
          {finance.costCenter.code} {finance.costCenter.name}
        </div>
      ) : (
        <div className="dpFormHint dpFormHint--warning">
          Guia sem rateio: calcule a folha da competência e salve a guia de novo.
        </div>
      )}
      {canOpen ? (
        <Link to={FIN_ENTRY_PAGE(finance.entryId)} className="dpFormHint" style={{ display: "block" }}>
          Abrir no Financeiro
        </Link>
      ) : null}
    </>
  );
}
